import { type ReactNode, useMemo } from 'react';
import {
  DEFAULT_COUNTRY_CODES,
  DEFAULT_PREFERRED_COUNTRY_CODES,
  Flag,
  Select,
} from '@repo/design-system-kit';
import type { CountryCode, SelectProps } from '@repo/design-system-kit';
import { useIntl } from 'react-intl';

interface CountryOption {
  code: CountryCode;
  countryName: string;
}

export interface SelectCountryProps
  extends Partial<Omit<SelectProps<CountryOption>, 'children' | 'withSearch'>> {
  countries?: CountryCode[];
  preferredCountries?: CountryCode[];
  blacklistedCountries?: CountryCode[];
  portalContainerId?: string;
}

function sortByPreferredCountries(
  preferredCountries: CountryCode[]
): (a: CountryOption, b: CountryOption) => number {
  return (a: CountryOption, b: CountryOption): number => {
    const aIndex = preferredCountries.indexOf(a.code);
    const bIndex = preferredCountries.indexOf(b.code);

    if (aIndex === -1 && bIndex !== -1) {
      return 1;
    }

    if (aIndex !== -1 && bIndex === -1) {
      return -1;
    }

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    return a.countryName.localeCompare(b.countryName);
  };
}

export function SelectCountry({
  blacklistedCountries,
  countries,
  preferredCountries,
  label,
  placeholder,
  portalContainerId,
  ...props
}: SelectCountryProps): ReactNode {
  const { formatMessage, formatDisplayName } = useIntl();

  const defaultLabel = formatMessage({ id: 'shared-kit.country-select.label' });
  const defaultPlaceholder = formatMessage({ id: 'shared-kit.country-select.placeholder' });
  const searchProps = {
    placeholder: formatMessage({ id: 'shared-kit.country-select.search_placeholder' }),
    emptyMessage: formatMessage({ id: 'shared-kit.country-select.no_matches_message' }),
  };

  const countryOptions = useMemo(() => {
    function formatAsOption(countryCode: CountryCode): CountryOption {
      return {
        code: countryCode,
        countryName: formatDisplayName(countryCode, { type: 'region' }) ?? countryCode,
      };
    }

    const countryCodes: CountryCode[] = countries ?? DEFAULT_COUNTRY_CODES;
    const blacklistedCountryCodes = blacklistedCountries ?? [];
    const preferredCountryCodes: CountryCode[] =
      preferredCountries ?? DEFAULT_PREFERRED_COUNTRY_CODES;

    return countryCodes
      .filter(countryCode => !blacklistedCountryCodes.includes(countryCode))
      .map(formatAsOption)
      .sort(sortByPreferredCountries(preferredCountryCodes));
  }, [formatDisplayName, blacklistedCountries, countries, preferredCountries]);

  return (
    <Select
      label={label ?? defaultLabel}
      placeholder={placeholder ?? defaultPlaceholder}
      portalContainerId={portalContainerId}
      withSearch={searchProps}
      {...props}
    >
      {countryOptions.map(({ code, countryName }) => (
        <Select.Option beforeContent={<Flag code={code} width={20} />} id={code} key={code}>
          {countryName}
        </Select.Option>
      ))}
    </Select>
  );
}
